import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterCheckboxItem } from './FilterCheckboxItem';

interface UserSentimentFilterProps {
  filterIndex: number;
}

/**
 * Renders checkboxes for filtering sessions by user sentiment
 */
export const UserSentimentFilter = ({ filterIndex }: UserSentimentFilterProps) => {
  const { t } = useTranslation();
  const { getValues, control } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  const userSentiment = useWatch({ control, name: `filters.${filterIndex}.userSentiment` }) || [];

  const handleSentimentToggle = (value: number, checked: boolean) => {
    const currentValues = Array.isArray(userSentiment) ? [...userSentiment] : [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter((v: number) => v !== value);

    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, userSentiment: newValues });
  };

  const sentimentOptions = [
    { value: 1, label: t('analytics.filters.sentiment.positive'), emoji: '😊' },
    { value: 2, label: t('analytics.filters.sentiment.neutral'), emoji: '😐' },
    { value: 3, label: t('analytics.filters.sentiment.negative'), emoji: '😞' },
  ];

  return (
    <div className="space-y-2">
      {sentimentOptions.map(({ value, label, emoji }) => {
        const isChecked = userSentiment.includes(value);

        return (
          <FilterCheckboxItem
            key={value}
            id={`sentiment-${value}`}
            checked={isChecked}
            onCheckedChange={(checked) => handleSentimentToggle(value, checked as boolean)}
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">{emoji}</span>
              <span className="text-sm">{label}</span>
            </div>
          </FilterCheckboxItem>
        );
      })}
    </div>
  );
};
